import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CustomObjectLookup } from 'src/app/models/customObjectLookup.model';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

interface CompareGroup {
  key: string;
  items: CustomObjectLookup[];
}

@Component({
  standalone: false,
  selector: 'app-compare-tab',
  templateUrl: './compare-tab.component.html',
  styleUrls: ['./compare-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareTabComponent implements OnChanges {
  @Input() endPoints: CustomObjectLookup[] = [];
  @Input() selectedEndPoints: CustomObjectLookup[] = [];
  @Input() lockedEndpointId: string = '';

  @Output() selectionChange = new EventEmitter<CustomObjectLookup[]>();
  @Output() apply = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<void>();

  searchQuery: string = '';
  nodeCount: number = 1;
  expandedGroups = new Set<string>();
  filteredGroups: CompareGroup[] = [];

  readonly trackByKey = (_i: number, g: CompareGroup) => g.key;
  readonly trackByIndex = trackByIndex;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['endPoints']) {
      this.rebuildGroups();
    }

    if (changes['selectedEndPoints']) {
      this.nodeCount = this.selectedEndPoints.length + 1;
    }
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.rebuildGroups();
  }

  toggleGroup(key: string): void {
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  isSelected(endpointId: string | number): boolean {
    return endpointId === this.lockedEndpointId || this.selectedEndPoints.some(e => e.id === endpointId);
  }

  isAtNodeLimit(): boolean {
    return this.selectedEndPoints.length >= 4;
  }

  onToggle(endpoint: CustomObjectLookup, checked: boolean): void {
    let updated = [...this.selectedEndPoints];
    if (checked) {
      if (!this.isSelected(endpoint.id) && updated.length < 4) {
        updated.push({ ...endpoint, selected: true });
      }
    } else {
      if (endpoint.id !== this.lockedEndpointId) {
        updated = updated.filter(e => e.id !== endpoint.id);
      }
    }
    this.selectionChange.emit(updated);
  }

  private rebuildGroups(): void {
    const q = this.searchQuery.toLowerCase().trim();
    const filtered = q
      ? this.endPoints.filter(e =>
          e.name.toLowerCase().includes(q) ||
          (e.customObjectName || '').toLowerCase().includes(q)
        )
      : this.endPoints;

    const groupMap = new Map<string, CustomObjectLookup[]>();
    filtered.forEach(e => {
      const key = e.customObjectName || 'Default';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(e);
    });

    this.filteredGroups = Array.from(groupMap.entries()).map(([key, items]) => ({ key, items }));

    if (q) {
      this.filteredGroups.forEach(g => this.expandedGroups.add(g.key));
    }
  }
}
